import React, { useState } from 'react';
import CustomDialog from 'components/CustomDialog';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useAppSelector } from 'hooks/useAppSelector';
import { clearRegisterSpots, registerSpotsNotification, RegisterSpotsNotificationParams } from 'store/slices/feedback';
import { useAppDispatch } from 'hooks/useAppDispatch';
import ErrorMessage from '../ErrorMessage';
import { useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); // Simple regex for email validation

const PleaseContribute = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const authd = useAppSelector((state) => state.auth);
    const map = useAppSelector((state) => state.map);
    const feedback = useAppSelector((state) => state.feedback);
    const [email, setEmail] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [showErrors, setShowErrors] = useState(false);
    const { bounds } = map;
    const { userLoginSuccess, jwt, tokenData } = authd;
    const isLoggedIn = userLoginSuccess && !!jwt;
    const { registeringNotifications, registerNotificationsSuccess, registerNotificationsError } = feedback;

    const handleNotifyClick = () => {
        setShowErrors(true);

        const emailToUse = isLoggedIn ? tokenData?.data?.email ?? '' : email;

        if (!isLoggedIn && !isValidEmail(emailToUse)) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        setEmailError('');
        if (!captchaToken) return;

        const spotsNotificationData = {
            swLat: bounds?.sw?.lat,
            swLng: bounds?.sw?.lng,
            neLat: bounds?.ne?.lat,
            neLng: bounds?.ne?.lng,
            email: emailToUse,
            captchaToken,
        } as RegisterSpotsNotificationParams;

        void dispatch(registerSpotsNotification(spotsNotificationData));
    };

    return (
        <CustomDialog
            processing={registeringNotifications}
            processingCopy='Setting up your notification zone 📍'
            error={registerNotificationsError}
            errorContent={
                <ErrorMessage specificError="We couldn't save your notification preferences. Please try again. 😞"/>
            }
            success={registerNotificationsSuccess}
            successContent={
                <Typography variant='body1' textAlign='center'>
                    We've saved the area you're currently viewing on the map!
                    You'll get an email if a new drone spot is shared within this visible region.
                    Happy flying! 🚁
                </Typography>
            }
            alternative={
                !registerNotificationsSuccess && !registerNotificationsError ? (
                    <>
                        <Typography variant='subtitle1' align='center' mb={2}>
                            No spots in this area... yet!
                        </Typography>
                        <Typography variant='body1' mb={1}>
                            Join the community by adding your own favorite spots, or stay in the loop with
                            notifications.
                        </Typography>
                        <Typography variant='body1'>
                            Prefer to keep your spots private? No worries!
                            We'll notify you if someone adds a new public spot within the visible map area you're
                            viewing now.
                        </Typography>
                    </>
                ) : null
            }
            title={registerNotificationsSuccess || registerNotificationsError ? 'Notifications' : 'Stay Updated!'}
            closeCopy={registerNotificationsSuccess || registerNotificationsError ? 'Close' : 'Maybe later'}
            onClose={() => {
                dispatch(clearRegisterSpots());
                history.push('/');
            }}
        >
            <Box my={3}>
                {isLoggedIn ? (
                    <Typography variant='body1'>
                        Would you like us to send notifications to your email: <em>{tokenData?.data?.email ?? ''}</em>?
                    </Typography>
                ) : (
                    <TextField
                        fullWidth
                        label='Email address'
                        size='medium'
                        type='text'
                        value={email}
                        onChange={(e) => setEmail(e?.target?.value)}
                        placeholder='Enter your email'
                        error={!!emailError}
                        helperText={emailError}
                    />
                )}
                <Box sx={{
                    mt: 1
                }}
                >
                    <Box mb={2}>
                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                            onChange={(token: string) => setCaptchaToken(token)}
                        />
                        {showErrors && !captchaToken && (
                            <Typography variant="caption" color="error">
                                Please verify you're human.
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant='contained'
                        onClick={handleNotifyClick}
                    >
                        Yes, Notify Me!
                    </Button>
                </Box>
            </Box>
        </CustomDialog>
    );
};

export default PleaseContribute;
